import { useState } from "react";
import { useTranslation } from "react-i18next";
import { approveAccount, disableAccount, enableAccount, rejectAccount, type Account, type ListedAccount, type PendingAccount } from "../../../api/accountAdministrationApi";
import { addGroupMember, removeGroupMember } from "../../../api/access/accessApi";
import type { ListedGroup } from "../../../api/access/accessModel";
import { revokeInvitation, type InvitationOffer } from "../../../api/invitationsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { systemGroupName, type GroupNamer } from "../../access";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import Field from "../../../ui/forms/Field";
import SegmentedControl from "../../../ui/navigation/SegmentedControl";
import ConfirmAction from "../../../ui/overlays/ConfirmAction";
import { FORM_COMPACT_CONTROL_CLASS, FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { accountStatus } from "./accountStatus";
import { offerLine } from "./offerWords";

type Shown = "all" | "approval" | "invited" | "disabled";

interface AccountsTableProps {
  accounts: readonly ListedAccount[];
  /** The accounts that wait, with what for and the invitation each came from. */
  pending: readonly PendingAccount[];
  /** The invitations nobody accepted yet. */
  invitations: readonly InvitationOffer[];
  /** The installation's groups people are put into: every one but Everyone. */
  groups: readonly ListedGroup[];
  namer: GroupNamer;
  /** An account was approved, rejected or put into or out of a group. */
  /** The signed-in administrator: their own account offers no Disable. */
  viewerId: string | null;
  onChanged: () => void;
  onRevoked: () => void;
}

/**
 * Every account of the installation and every open invitation in one table (Sol critique C9,
 * redesign R3-3 `admin-accounts`), as GitHub's organisation people list: status filters with
 * their counts, and each row's own actions: approving or rejecting an account that waits for
 * that, revoking an invitation, putting an active account into a group or taking it out
 * (F133), disabling an active account, asked first, and enabling a disabled one. Waiting accounts come first; the rest by name. History-only actors show as such.
 */
export default function AccountsTable({ accounts, pending, invitations, groups, namer, viewerId, onChanged, onRevoked }: AccountsTableProps) {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState("");
  const [shown, setShown] = useState<Shown>("all");
  const wanted = filter.trim().toLocaleLowerCase();
  const found = (...texts: readonly string[]) => wanted === "" || texts.some((text) => text.toLocaleLowerCase().includes(wanted));
  const waiting = new Map(pending.map((account) => [account.userId, account]));
  const ofStatus: Readonly<Record<Shown, (account: ListedAccount) => boolean>> = {
    all: () => true,
    approval: (account) => account.waitsFor === "approval",
    invited: () => false,
    disabled: (account) => account.state === "disabled",
  };
  const count = (status: Shown) => accounts.filter(ofStatus[status]).length + (status === "all" || status === "invited" ? invitations.length : 0);
  const rows = [...accounts]
    .filter((account) => ofStatus[shown](account) && found(account.displayName, account.email))
    .sort((left, right) => Number(right.waitsFor === "approval") - Number(left.waitsFor === "approval") || left.displayName.localeCompare(right.displayName, i18n.language));
  const invited = shown === "all" || shown === "invited" ? invitations.filter((invitation) => found(invitation.email)) : [];
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Field label={t("administration.accounts.filter")} labelHidden={true} className="min-w-48 flex-1">
          {(control) => (
            <input {...control} type="search" className={FORM_CONTROL_CLASS} placeholder={t("administration.accounts.filter")} value={filter} onChange={(event) => { setFilter(event.target.value); }} />
          )}
        </Field>
        <SegmentedControl<Shown> label={t("administration.accounts.shown")} selected={shown} onSelect={setShown}
          options={(["all", "approval", "invited", "disabled"] as const).map((status) => ({ key: status, label: t(`administration.accounts.filters.${status}`, { n: count(status) }) }))} />
      </div>
      {rows.length + invited.length === 0 ? <EmptyState>{t("administration.accounts.empty")}</EmptyState> : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="app-text-muted text-xs">
              <th scope="col" className="py-1 pr-3 font-semibold">{t("projects.settings.members.person")}</th>
              <th scope="col" className="py-1 pr-3 font-semibold">{t("administration.accounts.state")}</th>
              <th scope="col" className="py-1 pr-3 font-semibold">{t("administration.groups.title")}</th>
              <th scope="col" className="py-1"><span className="sr-only">{t("administration.accounts.actions")}</span></th>
            </tr>
          </thead>
          <tbody className="app-divide-y">
            {rows.filter((account) => account.waitsFor === "approval").map((account) => (
              <AccountRow key={account.userId} account={account} waiting={waiting.get(account.userId)} groups={groups} namer={namer} viewerId={viewerId} onChanged={onChanged} />
            ))}
            {invited.map((invitation) => <InvitationRow key={invitation.invitationId} invitation={invitation} onRevoked={onRevoked} />)}
            {rows.filter((account) => account.waitsFor !== "approval").map((account) => (
              <AccountRow key={account.userId} account={account} waiting={waiting.get(account.userId)} groups={groups} namer={namer} viewerId={viewerId} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** An open invitation: its state in State, the groups it carries in Groups, as an account's row has them (Sol critique C4). */
function InvitationRow({ invitation, onRevoked }: { invitation: InvitationOffer; onRevoked: () => void }) {
  const { t, i18n } = useTranslation();
  const { state, submit } = useSubmission();
  const carried = [...(invitation.accountRole === "administrator" ? [systemGroupName(t, "administrators")] : []), ...invitation.groupNames];
  const sent = [
    invitation.invitedBy === "" ? null : t("administration.invitations.by", { name: invitation.invitedBy }),
    t("administration.invitations.until", { time: formatDateTime(invitation.expiresAt, i18n.language) }),
  ].filter((part) => part !== null);
  return (
    <tr className="align-top">
      <td className="py-2 pr-3 font-semibold break-all">{invitation.email}</td>
      <td className="py-2 pr-3">
        <Chip>{t("administration.invitations.invited")}</Chip>
        <p className="app-text-muted mt-1 text-xs">{sent.join(" · ")}</p>
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap items-center gap-1">
          {carried.map((name) => <Chip key={name}>{name}</Chip>)}
          {invitation.projectName === null ? null : <span className="app-text-muted text-xs">{t("administration.invitations.into", { project: invitation.projectName })}</span>}
        </div>
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </td>
      <td className="py-2 text-right">
        <AppButton size="sm" variant="secondary" disabled={state.status === "submitting"}
          onClick={() => { submit(async () => { await revokeInvitation(invitation.invitationId); onRevoked(); }); }}>
          {t("administration.invitations.revoke")}
        </AppButton>
      </td>
    </tr>
  );
}

interface AccountRowProps {
  account: ListedAccount;
  /** What it waits for and where it came from, while it waits. */
  waiting: PendingAccount | undefined;
  groups: readonly ListedGroup[];
  namer: GroupNamer;
  /** The signed-in administrator: their own account offers no Disable. */
  viewerId: string | null;
  onChanged: () => void;
}

function AccountRow({ account, waiting, groups, namer, viewerId, onChanged }: AccountRowProps) {
  const { t, i18n } = useTranslation();
  const { state, submit } = useSubmission();
  const [adding, setAdding] = useState("");
  const [disabling, setDisabling] = useState(false);
  const isIn = (group: ListedGroup) => group.memberIds.includes(account.userId);
  const pathName = (group: ListedGroup) => namer.path(pathOf(group, groups));
  const joined = groups.filter(isIn);
  const others = groups.filter((group) => !isIn(group));
  const change = (work: () => Promise<unknown>) => {
    submit(async () => { await work(); setAdding(""); onChanged(); });
  };
  const decide = (decision: (userId: string) => Promise<Account>) => { change(() => decision(account.userId)); };
  const active = account.state === "active" && !account.invited;
  const pendingWork = state.status === "submitting";
  return (
    <tr className="align-top">
      <td className="py-2 pr-3">
        <p className="font-semibold">{account.displayName}</p>
        <p className="app-text-muted break-all text-xs">{account.email}</p>
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap gap-1">
          <Chip tone={account.waitsFor === "approval" ? "warning" : "neutral"}>{account.invited ? t("administration.accounts.historyOnly") : accountStatus(t, account.state, account.waitsFor)}</Chip>
          {account.accountRole === "administrator" ? <Chip>{t("profile.accountRoles.administrator")}</Chip> : null}
        </div>
        {waiting === undefined ? null : (
          <p className="app-text-muted mt-1 text-xs">
            {waiting.invitation === null
              ? t("accountApprovals.registeredAt", { time: formatDateTime(waiting.registeredAt, i18n.language) })
              : offerLine(t, waiting.invitation)}
          </p>
        )}
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap items-center gap-1">
          {joined.map((group) => (
            <span key={group.groupId} className="inline-flex items-center gap-0.5">
              <Chip>{pathName(group)}</Chip>
              {active ? (
                <AppButton variant="ghost" size="sm" disabled={pendingWork} aria-label={t("administration.accounts.takeOut", { group: namer.name(group.groupId) })}
                  onClick={() => { change(() => removeGroupMember(group.groupId, account.userId)); }}>×</AppButton>
              ) : null}
            </span>
          ))}
          {active && others.length > 0 ? (
            <Field label={t("administration.accounts.putIn", { name: account.displayName })} labelHidden={true}>
              {(control) => (
                <select {...control} className={FORM_COMPACT_CONTROL_CLASS} value={adding} disabled={pendingWork}
                  onChange={(event) => {
                    const groupId = event.target.value;
                    setAdding(groupId);
                    if (groupId !== "") change(() => addGroupMember(groupId, account.userId));
                  }}>
                  <option value="">{t("administration.accounts.addToGroup")}</option>
                  {others.map((group) => <option key={group.groupId} value={group.groupId}>{pathName(group)}</option>)}
                </select>
              )}
            </Field>
          ) : null}
        </div>
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </td>
      <td className="py-2 text-right">
        <div className="flex justify-end gap-2">
          {waiting === undefined ? null : (
            <>
              {waiting.waitsFor === "approval" ? (
                <AppButton size="sm" variant="primary" disabled={pendingWork} onClick={() => { decide(approveAccount); }}>{t("accountApprovals.approve")}</AppButton>
              ) : null}
              <AppButton size="sm" variant="danger" disabled={pendingWork} onClick={() => { decide(rejectAccount); }}>{t("accountApprovals.reject")}</AppButton>
            </>
          )}
          {active && account.userId !== viewerId ? (
            <AppButton size="sm" variant="secondary" disabled={pendingWork} onClick={() => { setDisabling(true); }}>{t("administration.accounts.disable")}</AppButton>
          ) : null}
          {account.state === "disabled" ? (
            <AppButton size="sm" variant="secondary" disabled={pendingWork} onClick={() => { decide(enableAccount); }}>{t("administration.accounts.enable")}</AppButton>
          ) : null}
        </div>
        {disabling ? (
          <ConfirmAction title={t("administration.accounts.disableTitle", { name: account.displayName })} confirmLabel={t("administration.accounts.disable")} tone="danger"
            onConfirm={async () => { await disableAccount(account.userId); onChanged(); }} onClose={() => { setDisabling(false); }}>
            <p>{t("administration.accounts.disableMessage", { name: account.displayName })}</p>
          </ConfirmAction>
        ) : null}
      </td>
    </tr>
  );
}

/** The ids from the outermost group around it down to the group itself. */
function pathOf(group: ListedGroup, groups: readonly ListedGroup[]): readonly string[] {
  const byId = new Map(groups.map((listed) => [listed.groupId, listed]));
  const ids = [group.groupId];
  for (let parent = group.parentId === null ? undefined : byId.get(group.parentId); parent !== undefined && !ids.includes(parent.groupId); parent = parent.parentId === null ? undefined : byId.get(parent.parentId)) {
    ids.unshift(parent.groupId);
  }
  return ids;
}
